import type { FunctionComponent } from 'react';
import type { ConnectedProps } from 'react-redux';

import _isNil from 'lodash-es/isNil';

import type { AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type { AssetsTranscriptionElementTypeSchema } from '@iconik-io/iconik-api/src/services/assets';
import type { SpeakerLabels } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import type { TimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';

import { fieldRenderer } from '../../../../components/fields/timecode/renderers';
import Button from '../../../../ui/button/button';
import SelectFieldWrapper from '../../../../ui/forms/field_wrappers/select/select';
import TextArea from '../../../../ui/forms/field_wrappers/textarea/textarea';
import TimecodeFieldWrapper from '../../../../ui/forms/field_wrappers/timecode/timecode';
import Form from '../../../../ui/forms/form';
import connector from './connector';

import styles from './transcription_segment_edit.module.css';

type Props = ConnectedProps<typeof connector> & {
  canEditSpeakers: boolean;
  speakerLabels: SpeakerLabels;
  segment: SegmentEntityWithIdNormalized;
  onCancel: () => void;
  timecodeOptions: TimecodeOptions | undefined;
};

type FormData = {
  speaker: string;
  text: string;
  time_end_milliseconds: number;
  time_start_milliseconds: number;
};

const TranscriptionSegmentEdit: FunctionComponent<Props> = (props) => (
  <div className={styles.root}>
    <Form
      onValidSubmit={(data: FormData): void => {
        const { segment } = props;
        if (
          !segment.id ||
          !segment.transcription_id ||
          !segment.transcription ||
          _isNil(data.time_start_milliseconds) ||
          _isNil(data.time_end_milliseconds)
        ) {
          return;
        }
        const speaker = parseInt(data.speaker, 10);
        const update: AssetsSegmentSchemaWithIdWithRequiredTranscriptionIdPatched =
          {
            time_start_milliseconds: data.time_start_milliseconds,
            time_end_milliseconds: data.time_end_milliseconds,
            transcription_id: segment.transcription_id,
            transcription: {
              words: segment.transcription.words,
              speaker,
            },
          };
        const timecodesChanged =
          data.time_start_milliseconds !== segment.time_start_milliseconds ||
          data.time_end_milliseconds !== segment.time_end_milliseconds;
        const textChanged = data.text !== segment.segment_text;
        if (data.text && (textChanged || timecodesChanged)) {
          const wordsArray = data.text.split(' ');
          const wordLength =
            (data.time_end_milliseconds - data.time_start_milliseconds) /
            wordsArray.length;
          const words: AssetsTranscriptionElementTypeSchema[] = wordsArray.map(
            (value, index) => ({
              start_ms: index * wordLength,
              end_ms: index * wordLength + wordLength,
              score: 1,
              value,
            }),
          );
          if (update.transcription) {
            update.transcription.words = words;
          }
          update.segment_text = data.text;
        }

        props
          .editTranscriptionSegmentById(segment.id, update)
          .then(props.onCancel);
      }}
    >
      <div className={styles.edit_header}>
        <div>
          {props.segment.transcription &&
          !_isNil(props.segment.transcription.speaker) ? (
            <div className={styles.speaker_field}>
              <SelectFieldWrapper
                name="speaker"
                options={Object.keys(props.speakerLabels).map((speakerId) => ({
                  label: props.speakerLabels[speakerId],
                  value: speakerId,
                }))}
                value={props.segment.transcription.speaker.toString()}
                disabled={!props.canEditSpeakers}
              />
            </div>
          ) : null}
        </div>

        {props.timecodeOptions ? (
          <div className={styles.time_codes}>
            <TimecodeFieldWrapper
              name="time_start_milliseconds"
              timecodeOptions={props.timecodeOptions}
              value={props.segment.time_start_milliseconds ?? undefined}
              aria={{
                label: 'Start timecode',
              }}
              renderer={fieldRenderer}
            />
            <span className={styles.separator}>–</span>
            <TimecodeFieldWrapper
              name="time_end_milliseconds"
              timecodeOptions={props.timecodeOptions}
              value={props.segment.time_end_milliseconds ?? undefined}
              aria={{
                label: 'End timecode',
              }}
              renderer={fieldRenderer}
            />
          </div>
        ) : undefined}
      </div>

      <TextArea
        autoSize={false}
        name="text"
        value={props.segment.segment_text ?? undefined}
      />

      <div className={styles.actions}>
        <div>
          <Button
            onClick={() =>
              props.segment.id
                ? props.deleteTranscriptionSegmentById(props.segment.id)
                : null
            }
            iconName="deletion_delete"
            variant="danger"
            wideBasis={false}
            noBorder
          />
        </div>

        <div>
          <Button variant="warning" noBorder onClick={props.onCancel}>
            Cancel
          </Button>
          <Button
            className={styles.save_button}
            variant="success"
            type="submit"
          >
            Save
          </Button>
        </div>
      </div>
    </Form>
  </div>
);

export default connector(TranscriptionSegmentEdit);
